import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent, getUpcomingBirthdays } from '../api/apiClient';
import socket from '../socket';
import './UpcomingEvents.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const pad = v => String(v).padStart(2, '0');
const toInput = dt => {
  const d = new Date(dt);
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const emptyForm = { title: '', description: '', location: '', start_date: '', end_date: '' };

const EventMenu = ({ event, isAdmin, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  if (!isAdmin) return null;

  return (
    <div className="ue-menu-wrap" ref={ref}>
      <button className="ue-menu-btn" onClick={() => setOpen(v => !v)}>⋯</button>
      {open && (
        <div className="ue-menu-dropdown">
          <button onClick={() => { onEdit(event); setOpen(false); }}>Edit</button>
          <button onClick={() => { onDelete(event.id); setOpen(false); }}>Delete</button>
        </div>
      )}
    </div>
  );
};

const UpcomingEvents = ({ currentUser }) => {
  const isAdmin = currentUser?.isAdmin;
  const navigate = useNavigate();
  const [events,    setEvents]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [formMode,  setFormMode]  = useState('add');
  const [formData,  setFormData]  = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const fetchEvents = useCallback(async () => {
    try {
      const now      = new Date();
      const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const in10     = new Date(todayMid); in10.setDate(in10.getDate() + 10);

      const [evRes, bdRes] = await Promise.all([getCalendarEvents(), getUpcomingBirthdays()]);

      const realEvents = (evRes.data || [])
        .filter(e => new Date(e.end_date || e.start_date) >= now)
        .map(e => ({ ...e, sortDate: new Date(e.start_date) }));

      // Birthdays as pseudo-events, but only within the next 10 days
      const birthdays = (bdRes.data || []).map(p => {
        const m = parseInt((p.birthday || '').slice(0, 2), 10); // 1-12
        const d = parseInt((p.birthday || '').slice(3, 5), 10);
        if (!m || !d) return null;
        let occ = new Date(now.getFullYear(), m - 1, d);
        if (occ < todayMid) occ = new Date(now.getFullYear() + 1, m - 1, d); // next occurrence
        return { id: `bday-${p.id}`, isBirthday: true, person: p, title: `${p.name}'s Birthday 🎂🎉!`, start_date: occ, sortDate: occ };
      }).filter(b => b && b.sortDate <= in10);

      const merged = [...realEvents, ...birthdays]
        .sort((a, b) => a.sortDate - b.sortDate)
        .slice(0, 8);
      setEvents(merged);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchEvents();
    socket.on('calendar:changed', fetchEvents);
    socket.on('staff:changed', fetchEvents);
    return () => {
      socket.off('calendar:changed', fetchEvents);
      socket.off('staff:changed', fetchEvents);
    };
  }, [fetchEvents]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!formData.title || !formData.start_date || !formData.end_date) return;
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        start_date: formData.start_date.replace('T', ' ') + ':00',
        end_date: formData.end_date.replace('T', ' ') + ':00',
        created_by: currentUser.id,
        is_admin: true,
      };
      if (formMode === 'edit') {
        await updateCalendarEvent(editingId, payload);
      } else {
        await createCalendarEvent(payload);
      }
      setShowForm(false); setFormData(emptyForm); setEditingId(null);
      fetchEvents();
    } catch (err) { console.error(err); }
  };

  const handleEdit = ev => {
    setFormData({
      title: ev.title,
      description: ev.description || '',
      location: ev.location || '',
      start_date: toInput(ev.start_date),
      end_date: toInput(ev.end_date || ev.start_date),
    });
    setEditingId(ev.id);
    setFormMode('edit');
    setShowForm(true);
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this event?')) return;
    try { await deleteCalendarEvent(id, { data: { is_admin: true } }); fetchEvents(); }
    catch (err) { console.error(err); }
  };

  const openAdd = () => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    const end = new Date(now); end.setHours(end.getHours() + 1);
    setFormData({ ...emptyForm, start_date: toInput(now), end_date: toInput(end) });
    setFormMode('add'); setEditingId(null); setShowForm(true);
  };

  return (
    <div className="ue-outer">
      <div className="ue-header">
        <h2 className="ue-heading">Upcoming Events</h2>
        <div className="ue-header-actions">
          {isAdmin && (
            <button className="ue-add-btn" onClick={showForm ? () => setShowForm(false) : openAdd}>
              {showForm ? '✕' : '+ Add'}
            </button>
          )}
          <button className="ue-view-all" onClick={() => navigate('/calendar')}>
            View all →
          </button>
        </div>
      </div>

      {showForm && isAdmin && (
        <form className="ue-form" onSubmit={handleSubmit}>
          <input className="ue-input" placeholder="Title" required
            value={formData.title} onChange={e => setFormData(f => ({ ...f, title: e.target.value }))} />
          <input className="ue-input" placeholder="Location (optional)"
            value={formData.location} onChange={e => setFormData(f => ({ ...f, location: e.target.value }))} />
          <input className="ue-input" type="datetime-local" required
            value={formData.start_date} onChange={e => setFormData(f => ({ ...f, start_date: e.target.value }))} />
          <input className="ue-input" type="datetime-local" required
            value={formData.end_date} onChange={e => setFormData(f => ({ ...f, end_date: e.target.value }))} />
          <button className="ue-submit" type="submit">{formMode === 'edit' ? 'Save' : 'Add Event'}</button>
        </form>
      )}

      {loading ? (
        <p className="ue-empty">Loading…</p>
      ) : events.length === 0 ? (
        <p className="ue-empty">No upcoming events.</p>
      ) : (
        <ul className="ue-list">
          {events.map(ev => {
            const d = new Date(ev.start_date);
            const dateBlock = (
              <div className="ue-date-block">
                <span className="ue-dow">{DAYS[d.getDay()]}</span>
                <span className="ue-day">{d.getDate()}</span>
                <span className="ue-mon">{MONTHS[d.getMonth()]}</span>
              </div>
            );
            if (ev.isBirthday) {
              return (
                <li key={ev.id} className="ue-item">
                  {dateBlock}
                  <div className="ue-info">
                    <span className="ue-title">{ev.title}</span>
                  </div>
                </li>
              );
            }
            return (
              <li key={ev.id} className="ue-item">
                {dateBlock}
                <div className="ue-info">
                  <span className="ue-title">{ev.title}</span>
                  {ev.location && <span className="ue-loc">📍 {ev.location}</span>}
                  <span className="ue-time">
                    {d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
                <EventMenu event={ev} isAdmin={isAdmin} onEdit={handleEdit} onDelete={handleDelete} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default UpcomingEvents;
